import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn } from '@/Components/motion';
import { Link } from 'react-router-dom';
import { Separator } from '@/Components/ui/separator';
import { siteConfig } from '@/config/site';

const sections = [
    {
        title: 'Overview',
        content:
            `${siteConfig.brand.name} is committed to the highest standards of Anti-Money Laundering (AML) and Know Your Customer (KYC) compliance. This policy outlines our procedures and measures to prevent and detect money laundering, terrorist financing, and other financial crimes. All employees, partners, and users of ${siteConfig.brand.name} are expected to comply with these policies and the applicable laws and regulations in the jurisdictions in which we operate.`,
    },
    {
        title: 'Customer Identification Program',
        content:
            `All customers are required to provide valid identification documents before accessing ${siteConfig.brand.name} services. Acceptable identification includes a government-issued photo ID (passport, national ID card, or driver\'s license), proof of address (utility bill, bank statement, or government correspondence dated within the last three months), and any additional documentation as required by applicable regulations. ${siteConfig.brand.name} reserves the right to request additional documentation at any time to verify your identity.`,
    },
    {
        title: 'Due Diligence',
        content:
            `${siteConfig.brand.name} conducts Customer Due Diligence (CDD) and, where appropriate, Enhanced Due Diligence (EDD) on all customers. Our due diligence process includes verifying customer identity against reliable and independent source documents, assessing the nature and purpose of the business relationship, screening customers against sanctions lists and politically exposed persons (PEP) databases, and conducting ongoing monitoring of customer transactions and behavior for consistency with their known profile.`,
    },
    {
        title: 'Ongoing Monitoring',
        content:
            `${siteConfig.brand.name} employs sophisticated transaction monitoring systems to detect suspicious activities and patterns. Our monitoring procedures include automated screening of transactions for unusual patterns, regular review of high-risk accounts and transactions, analysis of transaction volumes and frequencies against expected customer behavior, and investigation of any flagged transactions or activities. Any identified suspicious activity is escalated to our compliance team for further review.`,
    },
    {
        title: 'Suspicious Activity Reporting',
        content:
            `${siteConfig.brand.name} is obligated to report any suspicious transactions or activities to the relevant financial intelligence units and regulatory authorities as required by law. Our reporting procedures include filing Suspicious Activity Reports (SARs) in a timely manner, maintaining comprehensive records of all reports and supporting documentation, cooperating fully with law enforcement and regulatory investigations, and ensuring confidentiality of all reports filed. Employees are prohibited from tipping off customers about filed reports.`,
    },
    {
        title: 'Compliance',
        content:
            `${siteConfig.brand.name} maintains a robust compliance program that includes appointing a designated Money Laundering Reporting Officer (MLRO), providing regular AML/KYC training to all employees, conducting periodic independent audits of our AML/KYC procedures, keeping all policies and procedures up to date with evolving regulations, and maintaining comprehensive records for the period required by applicable law. Failure to comply with our AML/KYC policies may result in account suspension, termination, and reporting to relevant authorities.`,
    },
];

export default function AmlKycPolicy() {
    return (
        <AppLayout title={`AML/KYC Policy - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-4xl mx-auto">
                        <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                            Legal
                        </p>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            AML/KYC{' '}
                            <span className="text-gradient">Policy</span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                            Our commitment to preventing financial crime through
                            robust Anti-Money Laundering and Know Your Customer
                            procedures.
                        </p>
                    </div>
                </FadeIn>
            </Section>

            {/* Content */}
            <Section className="py-16 bg-muted/30 border-t border-border">
                <FadeIn>
                    <Card className="max-w-4xl mx-auto rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                        <CardContent className="p-8 md:p-12">
                            <p className="text-sm text-muted-foreground mb-10">
                                Last updated: March 2026
                            </p>

                            <div className="space-y-10">
                                {sections.map((section, index) => (
                                    <div key={section.title}>
                                        <h2 className="text-xl font-semibold text-foreground mb-3">
                                            {index + 1}. {section.title}
                                        </h2>
                                        <p className="text-muted-foreground leading-relaxed">
                                            {section.content}
                                        </p>
                                        {index < sections.length - 1 && (
                                            <Separator className="mt-10" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </FadeIn>
            </Section>

            {/* Bottom spacer */}
            <Section className="py-16 bg-background border-t border-border">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="text-sm text-muted-foreground">
                        Questions about our compliance policies?{' '}
                        <Link
                            to="/contact"
                            className="text-primary hover:underline"
                        >
                            Contact us
                        </Link>
                    </p>
                </div>
            </Section>
        </AppLayout>
    );
}
